import PageBreadcrumb from "../../../../shared/ui/common/PageBreadCrumb";
import UserMetaCard from "../../../../modules/user_profile/ui/components/UserMetaCard";
import UserInfoCard from "../../../../modules/user_profile/ui/components/UserInfoCard";
import PageMeta from "../../../../shared/ui/common/PageMeta";

import { useTherapist } from "../../../../context/useTherapist";
import UserDeleteAccountCard from "../../../../modules/user_profile/ui/components/UserDeleteAccountCard";
import SubscriptionStatusCard from "../../../../modules/user_profile/ui/components/SubscriptionStatusCard";
import { deleteTherapistAccount } from "../../application/therapistProfile";
import { useNavigate, useSearchParams } from "react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { User, Shield, UserCircle, Globe, Building2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import MfaSetup from "../components/MfaSetup";
import { PublicProfileSettingsCard } from "../../../public_directory";
import { useCenter } from "../../../center";
import { MyCenterCard, CenterConsentCard } from "../../../center_admin";

type ProfileTab = 'profile' | 'public' | 'center' | 'account';
const PROFILE_TABS: ProfileTab[] = ['profile', 'public', 'center', 'account'];


export default function UserProfile() {
  const { t } = useTranslation();
  const { therapist, setTherapist } = useTherapist();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  // The centre tab exists only for an account that belongs to one.
  const { membership } = useCenter();

  // Tab state synced with URL ?tab= parameter
  const [activeTab, setActiveTab] = useState<ProfileTab>(
    (searchParams.get('tab') as ProfileTab) || 'profile'
  );

  // Sync state if URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab') as ProfileTab;
    if (tabParam && PROFILE_TABS.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab: ProfileTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleDeleteAccount = async () => {
    if (!therapist?.id) return;

    const confirmDelete = window.confirm(
      t("profile.deleteConfirm")
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);

      await deleteTherapistAccount(therapist.id);

      // Clear context
      setTherapist(null);

      // Redirect user to login or landing page
      navigate("/login");
    } catch (error) {
      console.error("Error deleting therapist account:", error);
      alert(t("profile.deleteError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta />

      <PageBreadcrumb pageTitle={t("profile.pageTitle")} />

      <div className="max-w-4xl mx-auto space-y-10">
        <div className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 rounded-full bg-brand-500/10 text-brand-500 flex items-center justify-center shadow-theme-sm border border-brand-500/20">
            <UserCircle className="w-12 h-12" />
          </div>
          <div>
            <h3 className="text-3xl font-extrabold text-content-primary">
              {t("profile.sectionTitle")}
            </h3>
            <p className="text-content-tertiary mt-2 max-w-lg mx-auto">
              {t("profile.editInfoDesc")}
            </p>
          </div>
        </div>

        {/* ZEN PILL NAVIGATION */}
        <div className="flex justify-center">
          <div className="inline-flex items-center p-1.5 bg-gray-100/50 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-white/5 shadow-theme-sm">
            {[
              { id: 'profile', label: t("profile.tabs.profileData"), icon: User },
              { id: 'public', label: t("profile.tabs.publicProfile"), icon: Globe },
              ...(membership ? [{ id: 'center', label: t("profile.tabs.center"), icon: Building2 }] : []),
              { id: 'account', label: t("profile.tabs.account"), icon: Shield },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as ProfileTab)}
                className={`relative flex items-center gap-2.5 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === tab.id
                  ? "text-brand-600 dark:text-brand-400"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="active-pill-profile"
                    className="absolute inset-0 bg-white dark:bg-white/10 shadow-theme-sm rounded-xl border border-gray-200/50 dark:border-white/10"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <tab.icon className={`w-4 h-4 relative z-10 ${activeTab === tab.id ? "scale-110" : "opacity-70"}`} />
                <span className="relative z-10">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="space-y-8 pb-12"
          >
            {activeTab === 'profile' && (
              <div className="space-y-8">
                <div className="transform transition-all hover:translate-y-[-2px]">
                  <UserMetaCard therapist={therapist} />
                </div>
                <div className="transform transition-all hover:translate-y-[-2px]">
                  <UserInfoCard therapist={therapist} />
                </div>
              </div>
            )}

            {activeTab === 'public' && (
              <div className="space-y-8">
                <PublicProfileSettingsCard />
              </div>
            )}

            {activeTab === 'center' && membership && (
              <div className="space-y-8">
                <MyCenterCard membership={membership} />
                {/* The people this therapist brought with them, still to be
                    asked. Only somebody who treats has any. */}
                {membership.is_practising_therapist && (
                  <CenterConsentCard centerName={membership.center.name} />
                )}
              </div>
            )}

            {activeTab === 'account' && (
              <div className="space-y-8">
                <div className="transform transition-all hover:translate-y-[-2px]">
                  <MfaSetup />
                </div>
                <div className="transform transition-all hover:translate-y-[-2px]">
                  <SubscriptionStatusCard />
                </div>
                <div className="transform transition-all hover:translate-y-[-2px]">
                </div>
                <div className="transform transition-all hover:translate-y-[-2px]">
                </div>
                <div className="transform transition-all hover:translate-y-[-2px]">
                  <UserDeleteAccountCard onDelete={handleDeleteAccount} loading={loading} />
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  );
}
